import { combineBarcode, extractRawBarcode } from './barcodeUtils';

const text = value => String(value ?? '');
const owns = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

// These are the actual columns of the legacy full-product workbook. Names are
// display-only: they must never decide which product a row can overwrite.
export const readLegacyImportIdentity = row => {
  const itemNumber = text(row.料號);
  const rawStyleBarcode = text(row.樣式條碼).trim();
  return {
    itemNumber,
    rawStyleBarcode,
    styleBarcode: combineBarcode(rawStyleBarcode, itemNumber),
  };
};

export const legacyImportRowNumber = (row, index) => Number.isInteger(row.__rowNum__)
  ? row.__rowNum__ + 1 : index + 2;

const productCodes = product => {
  const itemNumber = text(product.itemNumber);
  const combined = Array.isArray(product.styleBarcodes) ? product.styleBarcodes.map(text) : [];
  const raw = Array.isArray(product.rawStyleBarcodes) ? product.rawStyleBarcodes.map(text) : [];
  return [...new Set([
    itemNumber,
    text(product.barcode),
    ...combined,
    ...raw,
    // Legacy products can contain only barcode+SKU, without rawStyleBarcodes.
    ...combined.map((barcode, index) => raw[index] || extractRawBarcode(barcode, itemNumber)),
  ].filter(Boolean))];
};

export const createLegacyImportIndex = (products = []) => {
  const byId = new Map();
  const byCode = new Map();
  const upsert = product => {
    if (!product.id) throw new Error('商品缺少識別 ID，請重新載入後再匯入');
    const old = byId.get(product.id);
    if (old) productCodes(old).forEach(code => byCode.get(code)?.delete(product.id));
    byId.set(product.id, product);
    productCodes(product).forEach(code => {
      if (!byCode.has(code)) byCode.set(code, new Set());
      byCode.get(code).add(product.id);
    });
  };
  products.forEach(upsert);
  return {
    upsert,
    find: row => {
      const { itemNumber, rawStyleBarcode, styleBarcode } = readLegacyImportIdentity(row);
      const ids = new Set();
      [itemNumber, rawStyleBarcode, styleBarcode].filter(Boolean).forEach(code => {
        byCode.get(code)?.forEach(id => ids.add(id));
      });
      return [...ids].map(id => byId.get(id));
    },
  };
};

const describeProduct = product => `${product.name || '未命名商品'}（料號 ${text(product.itemNumber) || '空白'}，ID ${product.id}）`;

export const inspectLegacyImportRow = (row, index, matches) => {
  const { itemNumber, rawStyleBarcode } = readLegacyImportIdentity(row);
  const label = `第 ${legacyImportRowNumber(row, index)} 列（${row.名稱 || itemNumber || rawStyleBarcode || '未命名'}）`;
  const problems = [];
  if (matches.length > 1) {
    problems.push(`料號／條碼對應多筆商品：${matches.map(describeProduct).join('、')}。請先確認重複商品，不能自動選擇或合併`);
  }
  const protectedProducts = matches.filter(product => ['styleIds', 'effects', 'dataStatus'].some(key => owns(product, key)));
  if (protectedProducts.length) {
    problems.push(`對應已使用直播備貨／功效／樣品保護的商品：${protectedProducts.map(describeProduct).join('、')}。請移除此列，改用商品編輯或直播備貨的功效匯入`);
  }
  if (matches.length === 1 && itemNumber && text(matches[0].itemNumber) && itemNumber !== text(matches[0].itemNumber)) {
    problems.push(`條碼已屬於其他料號：${describeProduct(matches[0])}。請修正料號／條碼，不能覆寫另一件商品`);
  }
  return problems.length ? `${label}：${problems.join('；')}` : '';
};

// Validate every imported row before any product OR vendor mutation is allowed.
// The persistence transaction still guards races after this read-only preflight.
export const assertLegacyImportPreflight = (rows, index) => {
  const issues = rows.map((row, rowIndex) => inspectLegacyImportRow(row, rowIndex, index.find(row))).filter(Boolean);
  if (issues.length) {
    throw Object.assign(new Error(`整份檔案未匯入，尚未新增廠商或修改商品。共 ${issues.length} 列需先處理，請查看下方列號與商品說明，移除或修正後再上傳。`), {
      code: 'legacy-import-preflight', issues, totalRows: rows.length,
    });
  }
};
